import { apiService } from './api';
import { preferencesService } from './preferences';
import { crossTabSync } from './crossTabSync';
import { User } from '@/types';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  authenticated?: boolean;
}

/**
 * Why a logout is happening. The backend uses this to decide whether to revoke the
 * Google grant: only `user` does. `idle` and `expired` end the session and clear the
 * stored tokens without sending the person back through Google's consent screen when
 * they return to their own desk.
 *
 * Sending nothing is fail-secure — the backend revokes. So the value is worth getting
 * right, but a missed call site degrades safely rather than silently.
 */
export type LogoutReason = 'user' | 'idle' | 'expired';

/**
 * HIPAA-Compliant Authentication Service
 *
 * Uses sessionStorage instead of localStorage for security:
 * - Session data is cleared when browser/tab is closed
 * - Users must re-authenticate for each browser session
 * - Prevents session persistence on shared/public computers
 */
export class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>('/auth/login', credentials);
    const { user } = response.data.data;

    // HIPAA: Use sessionStorage - clears when browser closes
    sessionStorage.setItem('user', JSON.stringify(user));

    // Broadcast login to other tabs
    crossTabSync.broadcast('LOGIN');

    return { user };
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>('/auth/register', data);
    const { user } = response.data.data;

    // HIPAA: Use sessionStorage - clears when browser closes
    sessionStorage.setItem('user', JSON.stringify(user));

    // Broadcast login to other tabs
    crossTabSync.broadcast('LOGIN');

    return { user };
  }

  async logout(reason: LogoutReason = 'user'): Promise<void> {
    try {
      await apiService.post('/auth/logout', { reason });
    } catch (error) {
      // Handle logout error if needed
      if (import.meta.env.DEV) {
        console.error('Logout error:', error);
      }
    } finally {
      // FIXED: Clear 'authToken' (not 'token') to match OAuth storage
      // Old bug: OAuth stored 'authToken', logout cleared 'token' → JWT survived logout!
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('authToken'); // FIXED: was 'token'
      sessionStorage.removeItem('token'); // Also clear old key for migration
      localStorage.removeItem('user');
      localStorage.removeItem('authToken'); // FIXED: was 'token'
      localStorage.removeItem('token'); // Also clear old key for migration

      // Clear user preferences cache
      preferencesService.clearCache();

      // CRITICAL: Broadcast logout to all other tabs/windows
      // This ensures users are logged out everywhere, not just the current tab
      crossTabSync.broadcast('LOGOUT');
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<User>('/auth/me');
    return response.data.data;
  }

  async checkSession(): Promise<AuthResponse> {
    try {
      const response = await apiService.get<{ authenticated: boolean; user: User }>('/auth/check');
      // Unwrap ApiResponse: response.data = { success, data }
      const authData = response.data.data;

      if (authData.authenticated && authData.user) {
        // HIPAA: Use sessionStorage
        sessionStorage.setItem('user', JSON.stringify(authData.user));
        return { user: authData.user, authenticated: true };
      }

      sessionStorage.removeItem('user');
      return { user: null as any, authenticated: false };
    } catch (error) {
      sessionStorage.removeItem('user');
      return { user: null as any, authenticated: false };
    }
  }

  async forgotPassword(email: string): Promise<void> {
    await apiService.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await apiService.post('/auth/reset-password', { token, password });
  }

  // Check if session exists (will make API call to verify)
  async verifyAuthentication(): Promise<boolean> {
    const response = await this.checkSession();
    return response.authenticated || false;
  }

  getStoredUser(): User | null {
    // HIPAA: Check sessionStorage first, then localStorage for migration
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (userStr) {
      // Migrate from localStorage to sessionStorage if needed
      if (localStorage.getItem('user')) {
        sessionStorage.setItem('user', userStr);
        localStorage.removeItem('user');
      }
      return JSON.parse(userStr);
    }
    return null;
  }

  // Quick check if user data exists locally (doesn't verify session)
  hasStoredUser(): boolean {
    return !!this.getStoredUser();
  }

  // Retired: GET /api/auth/users is 410 Gone.
  async getUsers(): Promise<User[]> {
    throw new Error('GET /api/auth/users has been removed. Use GET /api/clients/:clientId/assignable-users.');
  }
}

export const authService = new AuthService();
export default authService;